import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Debounced persistence for the editors (§35): local typing buffer, one PUT
 * per quiet period, `flush()` before leaving. `save` resolves to the server
 * state; a rejected save calls `onReject` so the buffer can revert.
 */
export function useAutosave<T>(save: (value: T) => Promise<void>, onReject: (err: unknown) => void, delayMs = 700) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<T | null>(null);
  const inFlight = useRef<Promise<void> | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const run = useCallback(async () => {
    if (pending.current === null) return;
    const value = pending.current;
    pending.current = null;
    setSaving(true);
    const p = save(value)
      .catch((err) => onReject(err))
      .finally(() => {
        setSaving(false);
        if (pending.current === null) setDirty(false);
      });
    inFlight.current = p;
    await p;
    inFlight.current = null;
    if (pending.current !== null) await run();
  }, [save, onReject]);

  const schedule = useCallback(
    (value: T) => {
      pending.current = value;
      setDirty(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        run();
      }, delayMs);
    },
    [run, delayMs]
  );

  const flush = useCallback(async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (inFlight.current) await inFlight.current;
    await run();
  }, [run]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  return { schedule, flush, saving, dirty };
}

export const apiMessage = (err: any, fallback: string): string =>
  err?.response?.data?.message || err?.message || fallback;
export const apiCode = (err: any): string | null => err?.response?.data?.code ?? null;
